/**
 * CR054: Admin → Prompts Library page
 * Manage reusable prompt definitions with lifecycle (archive/delete)
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, Search, Copy, Archive, Trash2, RotateCcw } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";

interface Prompt {
  id: string;
  key: string;
  name: string;
  phase: string;
  prompt_text: string;
  default_model_hint: string;
  max_output_tokens: number | null;
  temperature: number | null;
  enabled_by_default: boolean;
  is_active: boolean;
  archived_at: string | null;
  deleted_at: string | null;
  version: number;
  prompt_hash: string | null;
  output_schema_json: any | null;
  created_at: string;
  updated_at: string;
}

interface PromptUsage {
  packCount: number;
  snapshotCount: number;
}

const PHASES = ["SYSTEM", "SCHEMA", "READ", "READ_DETAILS", "SCAN", "SCAN_DETAILS", "MERGE", "EXTRACT", "NORMALIZE", "DEDUPE", "VALIDATE", "LOCATE_SNIPPET", "CROP_SNIPPET"] as const;
type PromptPhase = typeof PHASES[number];

const PHASE_COLORS: Record<string, string> = {
  SYSTEM: "bg-gray-500",
  SCHEMA: "bg-blue-500",
  READ: "bg-cyan-500",
  READ_DETAILS: "bg-teal-500",
  SCAN: "bg-green-500",
  SCAN_DETAILS: "bg-lime-500",
  MERGE: "bg-indigo-500",
  EXTRACT: "bg-yellow-500",
  NORMALIZE: "bg-purple-500",
  DEDUPE: "bg-pink-500",
  VALIDATE: "bg-orange-500",
  LOCATE_SNIPPET: "bg-rose-500",
  CROP_SNIPPET: "bg-rose-400",
};

export default function PromptsLibrary() {
  const { isAdmin } = useAuth();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  
  // Delete/Archive state
  const [promptToDelete, setPromptToDelete] = useState<Prompt | null>(null);
  const [promptUsage, setPromptUsage] = useState<PromptUsage | null>(null);
  const [checkingUsage, setCheckingUsage] = useState(false);

  // Form state
  const [formKey, setFormKey] = useState("");
  const [formName, setFormName] = useState("");
  const [formPhase, setFormPhase] = useState<PromptPhase>("SCAN");
  const [formText, setFormText] = useState("");
  const [formModel, setFormModel] = useState("gemini-2.5-flash-lite");
  const [formMaxTokens, setFormMaxTokens] = useState<string>("");
  const [formTemperature, setFormTemperature] = useState<string>("");
  const [formEnabledByDefault, setFormEnabledByDefault] = useState(true);
  const [formOutputSchema, setFormOutputSchema] = useState<string>("");
  const [outputSchemaError, setOutputSchemaError] = useState<string>("");

  const fetchPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from("prompts")
        .select("*")
        .is("deleted_at", null)
        .order("phase")
        .order("name");

      if (error) throw error;
      setPrompts(data || []);
    } catch (err) {
      console.error("Error fetching prompts:", err);
      toast.error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchPrompts();
    }
  }, [isAdmin]);

  const checkPromptUsage = async (promptId: string): Promise<PromptUsage> => {
    const [packResult, snapshotResult] = await Promise.all([
      supabase.from("prompt_pack_items").select("id", { count: "exact" }).eq("prompt_id", promptId),
      supabase.from("run_prompt_snapshots").select("id", { count: "exact" })
    ]);
    
    return {
      packCount: packResult.count || 0,
      snapshotCount: snapshotResult.count || 0
    };
  };

  const resetForm = () => {
    setFormKey("");
    setFormName("");
    setFormPhase("SCAN");
    setFormText("");
    setFormModel("gemini-2.5-flash-lite");
    setFormMaxTokens("");
    setFormTemperature("");
    setFormEnabledByDefault(true);
    setFormOutputSchema("");
    setOutputSchemaError("");
    setEditingPrompt(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormKey(prompt.key);
    setFormName(prompt.name);
    const validPhase = PHASES.includes(prompt.phase as PromptPhase) 
      ? (prompt.phase as PromptPhase) 
      : "SCAN";
    setFormPhase(validPhase);
    setFormText(prompt.prompt_text);
    setFormModel(prompt.default_model_hint);
    setFormMaxTokens(prompt.max_output_tokens?.toString() || "");
    setFormTemperature(prompt.temperature?.toString() || "");
    setFormEnabledByDefault(prompt.enabled_by_default);
    setFormOutputSchema(prompt.output_schema_json ? JSON.stringify(prompt.output_schema_json, null, 2) : "");
    setOutputSchemaError("");
    setShowDialog(true);
  };

  const handleDuplicate = (prompt: Prompt) => {
    setEditingPrompt(null);
    setFormKey(`${prompt.key}_copy`);
    setFormName(`${prompt.name} (Copy)`);
    const validPhase = PHASES.includes(prompt.phase as PromptPhase) 
      ? (prompt.phase as PromptPhase) 
      : "SCAN";
    setFormPhase(validPhase);
    setFormText(prompt.prompt_text);
    setFormModel(prompt.default_model_hint);
    setFormMaxTokens(prompt.max_output_tokens?.toString() || "");
    setFormTemperature(prompt.temperature?.toString() || "");
    setFormEnabledByDefault(prompt.enabled_by_default);
    setFormOutputSchema(prompt.output_schema_json ? JSON.stringify(prompt.output_schema_json, null, 2) : "");
    setOutputSchemaError("");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formKey.trim() || !formName.trim() || !formText.trim()) {
      toast.error("Key, name, and prompt text are required");
      return;
    }

    // Validate output schema JSON if provided
    let parsedSchema: any = null;
    if (formOutputSchema.trim()) {
      try {
        parsedSchema = JSON.parse(formOutputSchema.trim());
      } catch {
        setOutputSchemaError("Invalid JSON — fix syntax before saving");
        return;
      }
    }
    setOutputSchemaError("");

    setSaving(true);
    try {
      const data = {
        key: formKey.trim(),
        name: formName.trim(),
        phase: formPhase,
        prompt_text: formText,
        default_model_hint: formModel,
        max_output_tokens: formMaxTokens ? parseInt(formMaxTokens) : null,
        temperature: formTemperature ? parseFloat(formTemperature) : null,
        enabled_by_default: formEnabledByDefault,
        output_schema_json: parsedSchema,
      };

      if (editingPrompt) {
        const { error } = await supabase
          .from("prompts")
          .update(data)
          .eq("id", editingPrompt.id);

        if (error) throw error;
        toast.success("Prompt updated");
      } else {
        const { error } = await supabase.from("prompts").insert(data);

        if (error) throw error;
        toast.success("Prompt created");
      }

      setShowDialog(false);
      resetForm();
      fetchPrompts();
    } catch (err: any) {
      console.error("Error saving prompt:", err);
      if (err.code === "23505") {
        toast.error("A prompt with this key already exists");
      } else {
        toast.error("Failed to save prompt");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveClick = async (prompt: Prompt) => {
    setPromptToDelete(prompt);
    setCheckingUsage(true);
    const usage = await checkPromptUsage(prompt.id);
    setPromptUsage(usage);
    setCheckingUsage(false);
  };

  const handleArchive = async () => {
    if (!promptToDelete) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompts")
        .update({ archived_at: new Date().toISOString(), is_active: false })
        .eq("id", promptToDelete.id);

      if (error) throw error;
      toast.success("Prompt archived");
      setPromptToDelete(null);
      setPromptUsage(null);
      fetchPrompts();
    } catch (err) {
      console.error("Error archiving prompt:", err);
      toast.error("Failed to archive prompt");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (prompt: Prompt) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompts")
        .update({ archived_at: null, is_active: true })
        .eq("id", prompt.id);

      if (error) throw error;
      toast.success("Prompt restored");
      fetchPrompts();
    } catch (err) {
      console.error("Error restoring prompt:", err);
      toast.error("Failed to restore prompt");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!promptToDelete || !promptUsage) return;
    
    // Block if referenced
    if (promptUsage.packCount > 0 || promptUsage.snapshotCount > 0) {
      toast.error("Cannot delete - prompt is in use. Archive instead.");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("prompts")
        .delete()
        .eq("id", promptToDelete.id);

      if (error) throw error;
      toast.success("Prompt deleted");
      setPromptToDelete(null);
      setPromptUsage(null);
      fetchPrompts();
    } catch (err) {
      console.error("Error deleting prompt:", err);
      toast.error("Failed to delete prompt");
    } finally {
      setSaving(false);
    }
  };

  // Filter prompts
  const activePrompts = prompts.filter(p => !p.archived_at);
  const archivedPrompts = prompts.filter(p => !!p.archived_at);
  
  const filterPrompts = (list: Prompt[]) => {
    return list.filter(p => {
      const matchesSearch = 
        p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.phase.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPhase = phaseFilter === "all" || p.phase === phaseFilter;
      return matchesSearch && matchesPhase;
    });
  };

  const displayPrompts = activeTab === "active" 
    ? filterPrompts(activePrompts) 
    : filterPrompts(archivedPrompts);

  const canDelete = promptUsage && promptUsage.packCount === 0 && promptUsage.snapshotCount === 0;

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Prompts Library</h1>
            <p className="text-muted-foreground">
              Create and manage reusable prompt definitions
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New Prompt
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "active" | "archived")}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList>
              <TabsTrigger value="active">
                Active ({activePrompts.length})
              </TabsTrigger>
              <TabsTrigger value="archived">
                Archived ({archivedPrompts.length})
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-4">
              <Select value={phaseFilter} onValueChange={setPhaseFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All phases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All phases</SelectItem>
                  {PHASES.map((phase) => (
                    <SelectItem key={phase} value={phase}>{phase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <TabsContent value="active" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <PromptTable
                    prompts={displayPrompts}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onArchive={handleArchiveClick}
                    isArchived={false}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="archived" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <PromptTable
                    prompts={displayPrompts}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onRestore={handleRestore}
                    onDelete={handleArchiveClick}
                    isArchived={true}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPrompt ? "Edit Prompt" : "Create Prompt"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="key">Key</Label>
                  <Input
                    id="key"
                    value={formKey}
                    onChange={(e) => setFormKey(e.target.value)}
                    placeholder="gc_scan_page_v1"
                    disabled={!!editingPrompt}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Unique identifier (cannot be changed after creation)
                  </p>
                </div>
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Gift Card Page Scanner"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phase">Phase</Label>
                <Select value={formPhase} onValueChange={(v) => setFormPhase(v as PromptPhase)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHASES.map((phase) => (
                      <SelectItem key={phase} value={phase}>
                        {phase}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="prompt_text">Prompt Text</Label>
                <Textarea
                  id="prompt_text"
                  value={formText}
                  onChange={(e) => setFormText(e.target.value)}
                  placeholder="Enter the prompt instructions..."
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="model">Default Model</Label>
                  <Select value={formModel} onValueChange={setFormModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.5-flash-lite">Flash-Lite</SelectItem>
                      <SelectItem value="gemini-2.5-flash">Flash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="temperature">Temperature</Label>
                  <Input
                    id="temperature"
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={formTemperature}
                    onChange={(e) => setFormTemperature(e.target.value)}
                    placeholder="0.3"
                  />
                </div>
                <div>
                  <Label htmlFor="max_tokens">Max Tokens</Label>
                  <Input
                    id="max_tokens"
                    type="number"
                    value={formMaxTokens}
                    onChange={(e) => setFormMaxTokens(e.target.value)}
                    placeholder="2000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="output_schema">Output Schema (JSON, optional)</Label>
                <Textarea
                  id="output_schema"
                  value={formOutputSchema}
                  onChange={(e) => {
                    setFormOutputSchema(e.target.value);
                    if (outputSchemaError) setOutputSchemaError("");
                  }}
                  placeholder='{"type":"object","properties":{...}}'
                  className="min-h-[100px] font-mono text-xs"
                />
                {outputSchemaError && (
                  <p className="text-xs text-destructive mt-1">{outputSchemaError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Structured output schema sent to the model. Used by LOCATE_SNIPPET and workflow prompts.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="enabled_by_default"
                  checked={formEnabledByDefault}
                  onCheckedChange={setFormEnabledByDefault}
                />
                <Label htmlFor="enabled_by_default">Enabled by default in new packs</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingPrompt ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete/Archive Confirmation Dialog */}
        <AlertDialog open={!!promptToDelete} onOpenChange={(open) => !open && setPromptToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {promptToDelete?.archived_at ? "Delete Prompt" : "Archive Prompt"}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    {promptToDelete?.archived_at 
                      ? `Are you sure you want to permanently delete "${promptToDelete?.name}"?`
                      : `Are you sure you want to archive "${promptToDelete?.name}"?`
                    }
                  </p>
                  
                  {checkingUsage ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking usage...
                    </div>
                  ) : promptUsage && (
                    <div className="text-sm space-y-1 bg-muted p-3 rounded">
                      <p>• Used in <strong>{promptUsage.packCount}</strong> pack(s)</p>
                      <p>• Referenced in <strong>{promptUsage.snapshotCount}</strong> run snapshot(s)</p>
                      {!canDelete && promptToDelete?.archived_at && (
                        <p className="text-destructive font-medium mt-2">
                          Cannot delete: This prompt is in use. It will remain available for historical reference.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {promptToDelete?.archived_at ? (
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={!canDelete || saving}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Delete Permanently
                </AlertDialogAction>
              ) : (
                <AlertDialogAction onClick={handleArchive} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Archive
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

// Extracted table component for reuse
function PromptTable({
  prompts,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
  isArchived,
}: {
  prompts: Prompt[];
  onEdit: (p: Prompt) => void;
  onDuplicate: (p: Prompt) => void;
  onArchive?: (p: Prompt) => void;
  onRestore?: (p: Prompt) => void;
  onDelete?: (p: Prompt) => void;
  isArchived: boolean;
}) {
  if (prompts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {isArchived ? "No archived prompts" : "No prompts found"}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Key</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Phase</TableHead>
          <TableHead>Model</TableHead>
          <TableHead>Version</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {prompts.map((prompt) => (
          <TableRow key={prompt.id} className={isArchived ? "opacity-60" : ""}>
            <TableCell className="font-mono text-sm">
              {prompt.key}
            </TableCell>
            <TableCell className="font-medium">{prompt.name}</TableCell>
            <TableCell>
              <Badge className={PHASE_COLORS[prompt.phase] || "bg-gray-500"}>
                {prompt.phase}
              </Badge>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {prompt.default_model_hint.replace("gemini-2.5-", "")}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              v{prompt.version}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => onEdit(prompt)} title="Edit">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onDuplicate(prompt)} title="Duplicate">
                  <Copy className="h-4 w-4" />
                </Button>
                {isArchived ? (
                  <>
                    {onRestore && (
                      <Button variant="ghost" size="icon" onClick={() => onRestore(prompt)} title="Restore">
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                    {onDelete && (
                      <Button variant="ghost" size="icon" onClick={() => onDelete(prompt)} title="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                ) : (
                  onArchive && (
                    <Button variant="ghost" size="icon" onClick={() => onArchive(prompt)} title="Archive">
                      <Archive className="h-4 w-4" />
                    </Button>
                  )
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
